import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useAuth } from '../auth/AuthProvider'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import { perfilVeIndicadoresEscolas } from '../lib/perfilAcesso'
import { turnosFromEscola } from '../lib/turnosEscola'
import { supabase } from '../lib/supabase'
import { situacaoEntrega } from '../lib/entregaConformidade'
import { downloadText, formatCurrency, formatDate, monthBoundsISO, toCsv } from '../lib/utils'
import type { Contrato, Entrega, Escola, EscolaCeact } from '../types/database'
import { GRUPOS } from '../types/database'

const ESCOLAS_PAGE_SIZE = 20

type EscolaAlunosRow = Pick<
  Escola,
  | 'id'
  | 'nome'
  | 'zona'
  | 'rota_id'
  | 'alunos_creche'
  | 'alunos_medio'
  | 'alunos_ceact'
  | 'total_alunos'
  | 'infantil_m'
  | 'infantil_t'
  | 'infantil_n'
  | 'infantil_i'
  | 'fund_menor_m'
  | 'fund_menor_t'
  | 'fund_menor_n'
  | 'fund_menor_i'
  | 'fund_maior_m'
  | 'fund_maior_t'
  | 'fund_maior_n'
  | 'fund_maior_i'
  | 'eja_m'
  | 'eja_t'
  | 'eja_n'
  | 'eja_i'
> & {
  rota?: { nome: string }
  ceact?: Pick<EscolaCeact, 'total'> | null
}

function totaisModalidadeEscola(e: EscolaAlunosRow) {
  const infantil = turnosFromEscola(e as Escola, 'infantil').total
  const fundMenor = turnosFromEscola(e as Escola, 'fund_menor').total
  const fundMaior = turnosFromEscola(e as Escola, 'fund_maior').total
  const eja = turnosFromEscola(e as Escola, 'eja').total
  const creche = Number(e.alunos_creche) || 0
  const medio = Number(e.alunos_medio) || 0
  const ceact =
    e.ceact?.total != null
      ? Number(e.ceact.total) || 0
      : Number(e.alunos_ceact) || 0
  return {
    infantil,
    fundMenor,
    fundMaior,
    eja,
    creche,
    medio,
    ceact,
    total: Number(e.total_alunos) || 0,
  }
}

interface StatusEscola {
  escola_id: string
  escola: string
  rota: string | null
  ultima_entrega: string | null
  recebido_por: string | null
  status: string
}

type EntregaRow = Entrega & { escola?: { nome: string } }

function resetIndicadoresEscolas(
  setters: {
    setEscolasCount: (n: number) => void
    setAlunos: (n: number) => void
    setEntregasMes: (n: number) => void
    setAtendidas: (n: number) => void
    setDivergentesMes: (n: number) => void
    setConformidadeMes: (n: number) => void
    setPorGrupo: (v: { nome: string; qtd: number }[]) => void
    setPorRota: (v: { nome: string; qtd: number }[]) => void
    setEscolasAlunos: (v: EscolaAlunosRow[]) => void
    setStatusEscolas: (v: StatusEscola[]) => void
    setEntregas: (v: EntregaRow[]) => void
    setRotasCount: (n: number) => void
  },
) {
  setters.setEscolasCount(0)
  setters.setAlunos(0)
  setters.setEntregasMes(0)
  setters.setAtendidas(0)
  setters.setDivergentesMes(0)
  setters.setConformidadeMes(100)
  setters.setPorGrupo(GRUPOS.map((g) => ({ nome: g.label, qtd: 0 })))
  setters.setPorRota([])
  setters.setEscolasAlunos([])
  setters.setStatusEscolas([])
  setters.setEntregas([])
  setters.setRotasCount(0)
}

export function DashboardPage() {
  const { isAdmin, canViewValores, profile } = useAuth()
  const veIndicadoresEscolas = perfilVeIndicadoresEscolas(profile?.perfil)
  const [escolasCount, setEscolasCount] = useState(0)
  const [alunos, setAlunos] = useState(0)
  const [entregasMes, setEntregasMes] = useState(0)
  const [atendidas, setAtendidas] = useState(0)
  const [divergentesMes, setDivergentesMes] = useState(0)
  const [conformidadeMes, setConformidadeMes] = useState(100)
  const [porGrupo, setPorGrupo] = useState<{ nome: string; qtd: number }[]>([])
  const [porRota, setPorRota] = useState<{ nome: string; qtd: number }[]>([])
  const [escolasAlunos, setEscolasAlunos] = useState<EscolaAlunosRow[]>([])
  const [statusEscolas, setStatusEscolas] = useState<StatusEscola[]>([])
  const [entregas, setEntregas] = useState<EntregaRow[]>([])
  const [contrato, setContrato] = useState<Contrato | null>(null)
  const [rotasCount, setRotasCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [escolasPage, setEscolasPage] = useState(1)
  const [alunosPage, setAlunosPage] = useState(1)
  const [tab, setTab] = useState<'visao' | 'alunos' | 'entregas' | 'status'>('visao')

  const entregasSort = useTableSort<EntregaRow>('data', 'desc')
  const escolasSort = useTableSort<StatusEscola>('escola', 'asc')
  const alunosSort = useTableSort<EscolaAlunosRow>('nome', 'asc')

  useEffect(() => {
    document.title = 'Dashboard · ISEN EDUCAE'
    load()
    // Perfil já está resolvido quando a página monta (AuthProvider).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- montagem única, como o restante das pages
  }, [])

  useEffect(() => {
    setEscolasPage(1)
  }, [escolasSort.sortKey, escolasSort.sortDir])

  useEffect(() => {
    setAlunosPage(1)
  }, [alunosSort.sortKey, alunosSort.sortDir])

  async function load() {
    setLoading(true)
    setError(null)

    const ctrPromise = canViewValores
      ? supabase.from('contratos').select('*').limit(1).maybeSingle()
      : Promise.resolve({ data: null, error: null })

    // Fiscal: sem KPIs/abas de escolas (D-006). Só contrato + mensagem de escopo.
    if (!veIndicadoresEscolas) {
      const ctr = await ctrPromise
      resetIndicadoresEscolas({
        setEscolasCount,
        setAlunos,
        setEntregasMes,
        setAtendidas,
        setDivergentesMes,
        setConformidadeMes,
        setPorGrupo,
        setPorRota,
        setEscolasAlunos,
        setStatusEscolas,
        setEntregas,
        setRotasCount,
      })
      setContrato((ctr.data as Contrato) || null)
      if (ctr.error) setError(ctr.error.message)
      setTab('visao')
      setLoading(false)
      return
    }

    const isoStart = monthBoundsISO().inicio

    const [ind, entRota, status, esc, ceact, entMes, rot, ent, ctr, divMes] = await Promise.all([
      supabase.from('v_indicadores_resumo').select('*').maybeSingle(),
      supabase.from('v_entregas_por_rota').select('*'),
      supabase.from('v_status_por_escola').select('*'),
      supabase
        .from('escolas')
        .select(
          'id, nome, zona, rota_id, alunos_creche, alunos_medio, alunos_ceact, total_alunos, infantil_m, infantil_t, infantil_n, infantil_i, fund_menor_m, fund_menor_t, fund_menor_n, fund_menor_i, fund_maior_m, fund_maior_t, fund_maior_n, fund_maior_i, eja_m, eja_t, eja_n, eja_i, rota:rotas(nome)',
        )
        .order('nome'),
      supabase.from('escolas_ceact').select('escola_id, total'),
      supabase.from('entregas').select('escola_id, grupo, multiplos_grupos, rota_id').gte('data', isoStart),
      supabase.from('rotas').select('id', { count: 'exact', head: true }),
      supabase
        .from('entregas')
        .select('*, escola:escolas(nome)')
        .gte('data', isoStart)
        .order('data', { ascending: false })
        .limit(50),
      ctrPromise,
      supabase
        .from('entregas')
        .select('id, itens:entrega_itens(quantidade, quantidade_planejada)')
        .gte('data', isoStart),
    ])

    const errors: string[] = []
    if (ind.error) errors.push(ind.error.message)
    if (entRota.error) errors.push(entRota.error.message)
    if (status.error) errors.push(status.error.message)
    if (esc.error) errors.push(esc.error.message)
    if (ceact.error) errors.push(ceact.error.message)
    if (entMes.error) errors.push(entMes.error.message)
    if (rot.error) errors.push(rot.error.message)
    if (ent.error) errors.push(ent.error.message)
    if (ctr.error) errors.push(ctr.error.message)
    if (divMes.error) errors.push(divMes.error.message)

    setRotasCount(rot.count || 0)
    setEntregas((ent.data as EntregaRow[]) || [])
    setContrato((ctr.data as Contrato) || null)
    setStatusEscolas((status.data as StatusEscola[]) || [])

    const entregasConformidade = (divMes.data || []) as {
      id: string
      itens?: { quantidade: number; quantidade_planejada: number }[]
    }[]
    const nDiv = entregasConformidade.filter((e) => situacaoEntrega(e.itens) !== 'conforme').length
    const nTot = entregasConformidade.length
    setDivergentesMes(nDiv)
    setConformidadeMes(nTot ? Math.round(((nTot - nDiv) / nTot) * 100) : 100)

    const ceactByEscola = new Map(
      ((ceact.data || []) as { escola_id: string; total: number }[]).map((c) => [c.escola_id, c]),
    )

    const escolasList = ((esc.data || []) as unknown as EscolaAlunosRow[]).map((e) => ({
      ...e,
      ceact: ceactByEscola.get(e.id) || null,
    }))
    setEscolasAlunos(escolasList)

    const resumo = ind.data as {
      escolas?: number
      alunos?: number
      entregas_mes?: number
      escolas_atendidas?: number
    } | null

    const entregasResumo = (entMes.data || []) as {
      escola_id: string
      grupo: string | null
      multiplos_grupos: boolean
    }[]

    if (resumo) {
      setEscolasCount(Number(resumo.escolas) || 0)
      setAlunos(Number(resumo.alunos) || 0)
      setEntregasMes(Number(resumo.entregas_mes) || 0)
      setAtendidas(Number(resumo.escolas_atendidas) || 0)
    } else {
      setEscolasCount(escolasList.length)
      setAlunos(escolasList.reduce((s, e) => s + (e.total_alunos || 0), 0))
      setEntregasMes(entregasResumo.length)
      setAtendidas(new Set(entregasResumo.map((e) => e.escola_id)).size)
    }

    const gmap = new Map<string, number>()
    for (const g of GRUPOS) gmap.set(g.value, 0)
    for (const e of entregasResumo) {
      if (e.grupo) gmap.set(e.grupo, (gmap.get(e.grupo) || 0) + 1)
      else if (e.multiplos_grupos) gmap.set('secos', (gmap.get('secos') || 0) + 1)
    }
    setPorGrupo(GRUPOS.map((g) => ({ nome: g.label, qtd: gmap.get(g.value) || 0 })))

    if (entRota.error) {
      setPorRota([])
    } else {
      setPorRota(
        ((entRota.data || []) as { rota: string; qtd: number }[]).map((r) => ({
          nome: r.rota,
          qtd: Number(r.qtd),
        })),
      )
    }

    if (errors.length) {
      setError(
        status.error && !status.data
          ? errors[0] || 'Algumas views de indicadores podem estar indisponíveis'
          : errors.join(' · '),
      )
    }

    setLoading(false)
  }

  const cobertura = useMemo(
    () => (escolasCount ? Math.round((atendidas / escolasCount) * 100) : 0),
    [escolasCount, atendidas],
  )

  const gruposAtivos = porGrupo.filter((g) => g.qtd > 0).length

  const entregasSorted = useMemo(() => {
    const accessors: Record<string, (e: EntregaRow) => string | number> = {
      data: (e) => e.data,
      escola: (e) => e.escola?.nome || '',
      grupo: (e) =>
        e.multiplos_grupos
          ? 'Múltiplos'
          : GRUPOS.find((g) => g.value === e.grupo)?.label || '',
    }
    return entregasSort.sortRows(entregas, accessors)
  }, [entregas, entregasSort])

  const porModalidade = useMemo(() => {
    const acc = {
      infantil: 0,
      fundMenor: 0,
      fundMaior: 0,
      eja: 0,
      creche: 0,
      medio: 0,
      ceact: 0,
    }
    for (const e of escolasAlunos) {
      const t = totaisModalidadeEscola(e)
      acc.infantil += t.infantil
      acc.fundMenor += t.fundMenor
      acc.fundMaior += t.fundMaior
      acc.eja += t.eja
      acc.creche += t.creche
      acc.medio += t.medio
      acc.ceact += t.ceact
    }
    return [
      { nome: 'Infantil', qtd: acc.infantil },
      { nome: 'Fund. Menor', qtd: acc.fundMenor },
      { nome: 'Fund. Maior', qtd: acc.fundMaior },
      { nome: 'EJA', qtd: acc.eja },
      { nome: 'Creche', qtd: acc.creche },
      { nome: 'Médio', qtd: acc.medio },
      { nome: 'CEACT', qtd: acc.ceact },
    ]
  }, [escolasAlunos])

  const alunosSorted = useMemo(() => {
    const accessors: Record<string, (e: EscolaAlunosRow) => string | number> = {
      nome: (e) => e.nome,
      rota: (e) => e.rota?.nome || '',
      zona: (e) => e.zona,
      infantil: (e) => totaisModalidadeEscola(e).infantil,
      fundMenor: (e) => totaisModalidadeEscola(e).fundMenor,
      fundMaior: (e) => totaisModalidadeEscola(e).fundMaior,
      eja: (e) => totaisModalidadeEscola(e).eja,
      creche: (e) => totaisModalidadeEscola(e).creche,
      medio: (e) => totaisModalidadeEscola(e).medio,
      ceact: (e) => totaisModalidadeEscola(e).ceact,
      total: (e) => totaisModalidadeEscola(e).total,
    }
    return alunosSort.sortRows(escolasAlunos, accessors)
  }, [escolasAlunos, alunosSort])

  const totalAlunosPages = Math.max(1, Math.ceil(alunosSorted.length / ESCOLAS_PAGE_SIZE))

  const alunosPaginados = useMemo(() => {
    const start = (alunosPage - 1) * ESCOLAS_PAGE_SIZE
    return alunosSorted.slice(start, start + ESCOLAS_PAGE_SIZE)
  }, [alunosSorted, alunosPage])

  const alunosRangeStart = alunosSorted.length ? (alunosPage - 1) * ESCOLAS_PAGE_SIZE + 1 : 0
  const alunosRangeEnd = Math.min(alunosPage * ESCOLAS_PAGE_SIZE, alunosSorted.length)

  const statusSorted = useMemo(() => {
    const accessors: Record<string, (s: StatusEscola) => string | number> = {
      escola: (s) => s.escola,
      rota: (s) => s.rota || '',
      ultima_entrega: (s) => s.ultima_entrega || '',
      recebido_por: (s) => s.recebido_por || '',
      status: (s) => s.status,
    }
    return escolasSort.sortRows(statusEscolas, accessors)
  }, [statusEscolas, escolasSort])

  const totalEscolasPages = Math.max(1, Math.ceil(statusSorted.length / ESCOLAS_PAGE_SIZE))

  const escolasPaginadas = useMemo(() => {
    const start = (escolasPage - 1) * ESCOLAS_PAGE_SIZE
    return statusSorted.slice(start, start + ESCOLAS_PAGE_SIZE)
  }, [statusSorted, escolasPage])

  const escolasRangeStart = statusSorted.length ? (escolasPage - 1) * ESCOLAS_PAGE_SIZE + 1 : 0
  const escolasRangeEnd = Math.min(escolasPage * ESCOLAS_PAGE_SIZE, statusSorted.length)

  function exportCsv() {
    downloadText(
      'dashboard-escolas.csv',
      toCsv(
        statusEscolas.map((s) => ({
          Escola: s.escola,
          Rota: s.rota,
          'Última entrega': s.ultima_entrega,
          Recebedor: s.recebido_por,
          Status: s.status,
        })),
      ),
    )
  }

  if (loading) return <div className="loading">Carregando dashboard…</div>

  if (!veIndicadoresEscolas) {
    return (
      <div>
        <div className="page-header">
          <h1>Dashboard</h1>
          <div className="page-actions">
            <button type="button" className="btn btn-secondary" onClick={load}>
              Atualizar
            </button>
          </div>
        </div>
        {error && <div className="alert alert-warning">{error}</div>}

        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          Nutricionista ISEN: sem indicadores municipais de escolas no Dashboard. Use Ficha
          técnica, Pedido (planejado × previsto e Validar), Contrato e Auditoria.
        </div>

        <div className="kpi-grid">
          {canViewValores && (
            <div className="kpi">
              <div className="kpi-label">Contrato</div>
              <div className="kpi-value" style={{ fontSize: 20 }}>
                {contrato ? formatCurrency(Number(contrato.valor_total)) : '—'}
              </div>
              <div className="kpi-sub">{contrato?.fornecedor || 'Sem contrato'}</div>
            </div>
          )}
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: 16, marginBottom: 12 }}>Atalhos — Nutricionista ISEN</h2>
          <div className="page-actions" style={{ justifyContent: 'flex-start', gap: 8 }}>
            <Link to="/ficha-tecnica" className="btn btn-primary">
              Ficha técnica
            </Link>
            <Link to="/pedido-mensal" className="btn btn-secondary">
              Pedido
            </Link>
            <Link to="/contrato" className="btn btn-secondary">
              Contrato PNAE
            </Link>
            <Link to="/auditoria" className="btn btn-secondary">
              Auditoria
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <div className="page-actions">
          <button type="button" className="btn btn-secondary" onClick={load}>
            Atualizar
          </button>
          <button type="button" className="btn btn-secondary" onClick={exportCsv}>
            Exportar CSV
          </button>
        </div>
      </div>
      {error && <div className="alert alert-warning">{error}</div>}

      <div className="kpi-grid">
        <div className="kpi">
          <div className="kpi-label">Escolas</div>
          <div className="kpi-value">{escolasCount}</div>
          <div className="kpi-sub">{rotasCount} rotas</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Alunos</div>
          <div className="kpi-value">{alunos.toLocaleString('pt-BR')}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Cobertura</div>
          <div className="kpi-value">{cobertura}%</div>
          <div className="kpi-sub">
            {atendidas}/{escolasCount} escolas
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Entregas no mês</div>
          <div className="kpi-value">{entregasMes}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Conformidade</div>
          <div className="kpi-value">{conformidadeMes}%</div>
          <div className="kpi-sub">
            {divergentesMes} divergência{divergentesMes === 1 ? '' : 's'} ·{' '}
            <Link to="/acompanhamento">Ver relatório</Link>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Grupos ativos</div>
          <div className="kpi-value">{gruposAtivos}/5</div>
        </div>
        {canViewValores && (
          <div className="kpi">
            <div className="kpi-label">Contrato</div>
            <div className="kpi-value" style={{ fontSize: 20 }}>
              {contrato ? formatCurrency(Number(contrato.valor_total)) : '—'}
            </div>
            <div className="kpi-sub">{contrato?.fornecedor || 'Sem contrato'}</div>
          </div>
        )}
      </div>

      <div className="tabs">
        <button type="button" className={tab === 'visao' ? 'tab active' : 'tab'} onClick={() => setTab('visao')}>
          Visão geral
        </button>
        <button type="button" className={tab === 'alunos' ? 'tab active' : 'tab'} onClick={() => setTab('alunos')}>
          Alunos por escola
        </button>
        <button type="button" className={tab === 'entregas' ? 'tab active' : 'tab'} onClick={() => setTab('entregas')}>
          Últimas entregas
        </button>
        <button type="button" className={tab === 'status' ? 'tab active' : 'tab'} onClick={() => setTab('status')}>
          Status por escola
        </button>
      </div>

      {tab === 'visao' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card">
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>Grupos alimentares</h2>
            <div style={{ height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={porGrupo}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="qtd" fill="#2E7D32" name="Entregas" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card">
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>Entregas por rota</h2>
            <div style={{ height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={porRota}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="qtd" fill="#1565C0" name="Entregas" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {tab === 'alunos' && (
        <div className="card">
          <div className="page-header" style={{ marginBottom: 12, padding: 0 }}>
            <h2 style={{ fontSize: 16, margin: 0 }}>Quantitativo de alunos</h2>
            {isAdmin && (
              <Link to="/configuracoes?tab=escolas" className="kpi-sub" style={{ marginLeft: 'auto' }}>
                Editar quantitativo em Configurações → Escolas
              </Link>
            )}
          </div>
          <div style={{ height: 240, marginBottom: 16 }}>
            <ResponsiveContainer>
              <BarChart data={porModalidade}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="qtd" fill="#6A1B9A" name="Alunos" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div
            className="kpi-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 8,
              marginBottom: 16,
            }}
          >
            {porModalidade.map((m) => (
              <div key={m.nome} className="kpi" style={{ padding: '10px 12px' }}>
                <div className="kpi-sub">{m.nome}</div>
                <div className="kpi-value" style={{ fontSize: 20 }}>
                  {m.qtd.toLocaleString('pt-BR')}
                </div>
              </div>
            ))}
            <div className="kpi" style={{ padding: '10px 12px' }}>
              <div className="kpi-sub">Total</div>
              <div className="kpi-value" style={{ fontSize: 20 }}>
                {alunos.toLocaleString('pt-BR')}
              </div>
            </div>
          </div>
          {alunosSorted.length > 0 && (
            <div className="table-toolbar" style={{ marginBottom: 8 }}>
              <span className="kpi-sub">
                Exibindo {alunosRangeStart}–{alunosRangeEnd} de {alunosSorted.length}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={alunosPage <= 1}
                onClick={() => setAlunosPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <span className="kpi-sub">
                Página {alunosPage} de {totalAlunosPages}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={alunosPage >= totalAlunosPages}
                onClick={() => setAlunosPage((p) => Math.min(totalAlunosPages, p + 1))}
              >
                Próxima
              </button>
            </div>
          )}
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh label="Nome" sortKey="nome" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="name" />
                  <SortableTh label="Rota" sortKey="rota" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="text" />
                  <SortableTh label="Zona" sortKey="zona" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="compact" />
                  <SortableTh label="Infantil" sortKey="infantil" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="num" />
                  <SortableTh label="Fund. Menor" sortKey="fundMenor" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="num" />
                  <SortableTh label="Fund. Maior" sortKey="fundMaior" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="num" />
                  <SortableTh label="EJA" sortKey="eja" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="num" />
                  <SortableTh label="Creche" sortKey="creche" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="num" />
                  <SortableTh label="Médio" sortKey="medio" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="num" />
                  <SortableTh label="CEACT" sortKey="ceact" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="num" />
                  <SortableTh label="Total" sortKey="total" activeKey={alunosSort.sortKey} direction={alunosSort.sortDir} onSort={alunosSort.toggleSort} col="num" />
                </tr>
              </thead>
              <tbody>
                {alunosPaginados.map((e) => {
                  const t = totaisModalidadeEscola(e)
                  return (
                    <tr key={e.id}>
                      <td className="col-name">{e.nome}</td>
                      <td className="col-text">{e.rota?.nome || '—'}</td>
                      <td className="col-compact">{e.zona}</td>
                      <td className="col-num">{t.infantil}</td>
                      <td className="col-num">{t.fundMenor}</td>
                      <td className="col-num">{t.fundMaior}</td>
                      <td className="col-num">{t.eja}</td>
                      <td className="col-num">{t.creche}</td>
                      <td className="col-num">{t.medio}</td>
                      <td className="col-num">{t.ceact}</td>
                      <td className="col-num">
                        <strong>{t.total}</strong>
                      </td>
                    </tr>
                  )
                })}
                {!alunosSorted.length && (
                  <tr>
                    <td colSpan={11} className="kpi-sub">
                      Nenhuma escola cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'entregas' && (
        <div className="card">
          <div className="page-header" style={{ marginBottom: 12, padding: 0 }}>
            <h2 style={{ fontSize: 16, margin: 0 }}>Últimas entregas</h2>
            <Link to="/acompanhamento" className="kpi-sub" style={{ marginLeft: 'auto' }}>
              Ver relatório completo
            </Link>
          </div>
          {!entregas.length ? (
            <div className="empty-state">Nenhuma entrega neste mês.</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <SortableTh
                      label="Data"
                      sortKey="data"
                      activeKey={entregasSort.sortKey}
                      direction={entregasSort.sortDir}
                      onSort={entregasSort.toggleSort}
                      col="date"
                    />
                    <SortableTh
                      label="Escola"
                      sortKey="escola"
                      activeKey={entregasSort.sortKey}
                      direction={entregasSort.sortDir}
                      onSort={entregasSort.toggleSort}
                      col="name"
                    />
                    <SortableTh
                      label="Grupo"
                      sortKey="grupo"
                      activeKey={entregasSort.sortKey}
                      direction={entregasSort.sortDir}
                      onSort={entregasSort.toggleSort}
                      col="compact"
                    />
                  </tr>
                </thead>
                <tbody>
                  {entregasSorted.slice(0, 10).map((e) => (
                    <tr key={e.id}>
                      <td className="col-date">{formatDate(e.data)}</td>
                      <td className="col-name">{e.escola?.nome || '—'}</td>
                      <td className="col-compact">
                        {e.multiplos_grupos
                          ? 'Múltiplos'
                          : GRUPOS.find((g) => g.value === e.grupo)?.label || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'status' && (
        <div className="card">
          <div className="page-header" style={{ marginBottom: 12, padding: 0 }}>
            <h2 style={{ fontSize: 16, margin: 0 }}>Status por escola</h2>
            {statusSorted.length > 0 && (
              <div className="table-toolbar">
                <span className="kpi-sub">
                  Exibindo {escolasRangeStart}–{escolasRangeEnd} de {statusSorted.length}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={escolasPage <= 1}
                  onClick={() => setEscolasPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
                <span className="kpi-sub">
                  Página {escolasPage} de {totalEscolasPages}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={escolasPage >= totalEscolasPages}
                  onClick={() => setEscolasPage((p) => Math.min(totalEscolasPages, p + 1))}
                >
                  Próxima
                </button>
              </div>
            )}
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh
                    label="Escola"
                    sortKey="escola"
                    activeKey={escolasSort.sortKey}
                    direction={escolasSort.sortDir}
                    onSort={escolasSort.toggleSort}
                    col="name"
                  />
                  <SortableTh
                    label="Rota"
                    sortKey="rota"
                    activeKey={escolasSort.sortKey}
                    direction={escolasSort.sortDir}
                    onSort={escolasSort.toggleSort}
                    col="text"
                  />
                  <SortableTh
                    label="Última entrega"
                    sortKey="ultima_entrega"
                    activeKey={escolasSort.sortKey}
                    direction={escolasSort.sortDir}
                    onSort={escolasSort.toggleSort}
                    col="date"
                  />
                  <SortableTh
                    label="Recebedor"
                    sortKey="recebido_por"
                    activeKey={escolasSort.sortKey}
                    direction={escolasSort.sortDir}
                    onSort={escolasSort.toggleSort}
                    col="text"
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeKey={escolasSort.sortKey}
                    direction={escolasSort.sortDir}
                    onSort={escolasSort.toggleSort}
                    col="status"
                  />
                </tr>
              </thead>
              <tbody>
                {escolasPaginadas.map((s) => (
                  <tr key={s.escola_id}>
                    <td className="col-name">{s.escola}</td>
                    <td className="col-text">{s.rota || '—'}</td>
                    <td className="col-date">{s.ultima_entrega ? formatDate(s.ultima_entrega) : '—'}</td>
                    <td className="col-text">{s.recebido_por || '—'}</td>
                    <td className="col-status">
                      <span
                        className={s.status === 'Entregue' ? 'badge badge-ok' : 'badge badge-atencao'}
                      >
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {!statusSorted.length && (
                  <tr>
                    <td colSpan={5} className="kpi-sub">
                      Nenhuma escola cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
